import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Snowflake,
  ArrowLeft,
  Copy,
  Check,
  Info,
  MessageCircle,
  Bot,
  Globe,
  ExternalLink,
  X,
  CheckCircle2,
  Code2,
  Wallet
} from 'lucide-react';
import '../styles/App.css';
import '../styles/Docs.css';

const BASE_URL_EXAMPLE = 'https://iceswap.io/';

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard permission denied — nothing more we can do here
    }
  };

  return (
    <div className="docs-code-wrap">
      <pre className="docs-code-block">{code}</pre>

      <button
        className="docs-copy-btn"
        onClick={copy}
        aria-label="Copy code"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
};

const ParamTable = ({ params }) => (
  <table className="docs-param-table">
    <thead>
      <tr>
        <th>Field</th>
        <th>Type</th>
        <th>Required</th>
        <th>Description</th>
      </tr>
    </thead>

    <tbody>
      {params.map((p) => (
        <tr key={p.name}>
          <td className="docs-param-name">{p.name}</td>

          <td>{p.type}</td>

          <td>
            {p.required ? (
              <span className="docs-param-required">
                required
              </span>
            ) : (
              <span className="docs-param-optional">
                optional
              </span>
            )}
          </td>

          <td className="docs-param-desc">
            {p.desc}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Endpoint = ({ ep }) => (
  <section className="docs-endpoint" id={ep.id}>
    <div className="docs-endpoint-header">
      <span
        className={`docs-method-badge ${ep.method.toLowerCase()}`}
      >
        {ep.method}
      </span>

      <span className="docs-endpoint-path">
        {ep.path}
      </span>
    </div>

    <h3 className="docs-endpoint-title">
      {ep.title}
    </h3>

    <p className="docs-endpoint-desc">
      {ep.description}
    </p>

    {ep.pathParams && (
      <>
        <div className="docs-subheading">
          Path parameters
        </div>

        <ParamTable params={ep.pathParams} />
      </>
    )}

    {ep.body && (
      <>
        <div className="docs-subheading">
          Request body (JSON)
        </div>

        <ParamTable params={ep.body} />
      </>
    )}

    {ep.exampleRequest && (
      <>
        <div className="docs-subheading">
          Example request
        </div>

        <CodeBlock code={ep.exampleRequest} />
      </>
    )}

    <div className="docs-subheading">
      Example response
    </div>

    <span className="docs-status-tag">
      200 OK
    </span>

    <CodeBlock code={ep.exampleResponse} />

    {ep.notes &&
      ep.notes.map((n, i) => (
        <div className="docs-note info" key={i}>
          <Info size={15} />
          <span>{n}</span>
        </div>
      ))}
  </section>
);

const SECTIONS = [
  {
    title: 'Standard swap',

    endpoints: [
      {
        id: 'assets',
        method: 'GET',
        path: '/api/assets',
        title: 'List supported coins & networks',

        description:
          'Every coin Ice Swap can swap, grouped by network. Use this to populate a coin picker.',

        exampleResponse: `{
  "chains": {
    "Bitcoin": ["BTC", "WBTC"],
    "Ethereum": ["ETH", "USDT", "USDC", "DAI"],
    "Solana": ["SOL", "USDC_SOL", "USDT_SOL"],
    "Monero": ["XMR"]
  },
  "xmr_pairs": ["BTC_XMR", "XMR_BTC", "ETH_XMR", "XMR_ETH"],
  "privacy_chains": ["Monero"]
}`,

        notes: [
          'This excerpt is trimmed for readability — the live response lists every network we support, not just the four shown here.'
        ]
      },

      {
        id: 'estimate',
        method: 'POST',
        path: '/api/estimate',
        title: 'Get a live rate',

        description:
          'Price a pair without creating an order. Use this to show a rate before the user commits to anything.',

        body: [
          {
            name: 'from_asset',
            type: 'string',
            required: true,
            desc: 'Symbol the user is sending, e.g. "BTC".'
          },

          {
            name: 'to_asset',
            type: 'string',
            required: true,
            desc: 'Symbol the user wants to receive.'
          },

          {
            name: 'amount',
            type: 'number',
            required: true,
            desc: 'Amount, interpreted per amount_type.'
          },

          {
            name: 'amount_type',
            type: '"crypto" | "usd"',
            required: false,
            desc: 'Defaults to "crypto". Use "usd" to let the user enter a dollar amount instead.'
          }
        ],

        exampleRequest: `POST /api/estimate
Content-Type: application/json

{
  "from_asset": "BTC",
  "to_asset": "ETH",
  "amount": 0.05,
  "amount_type": "crypto"
}`,

        exampleResponse: `{
  "from_asset": "BTC",
  "from_amount": 0.05,
  "from_amount_usd": null,
  "to_asset": "ETH",
  "to_amount": "0.84219300",
  "rate": 16.8439,
  "provider": "2"
}`,

        notes: [
          '"provider" is an internal routing id — it tells you which of our liquidity routes priced this pair. It\'s informational only and safe to ignore.'
        ]
      },

      {
        id: 'quote',
        method: 'POST',
        path: '/api/quote',
        title: 'Create an order',

        description:
          'Creates a real order and returns a deposit address. This locks in a rate and starts the clock — only call it once your user is ready to send funds.',

        body: [
          {
            name: 'from_asset',
            type: 'string',
            required: true,
            desc: 'Symbol being sent.'
          },

          {
            name: 'to_asset',
            type: 'string',
            required: true,
            desc: 'Symbol being received.'
          },

          {
            name: 'amount',
            type: 'number',
            required: true,
            desc: 'Amount, interpreted per amount_type.'
          },

          {
            name: 'amount_type',
            type: '"crypto" | "usd"',
            required: false,
            desc: 'Defaults to "crypto".'
          },

          {
            name: 'recipient',
            type: 'string',
            required: true,
            desc: 'Address that receives the swapped coin. Minimum 10 characters.'
          },

          {
            name: 'refund',
            type: 'string',
            required: true,
            desc: 'Address refunded to if the swap can\'t complete. Minimum 10 characters.'
          }
        ],

        exampleRequest: `POST /api/quote
Content-Type: application/json

{
  "from_asset": "BTC",
  "to_asset": "ETH",
  "amount": 0.05,
  "recipient": "0x9F3C9a9E9b9C1234567890abcdef1234567890a",
  "refund": "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh"
}`,

        exampleResponse: `{
  "order_id": "aZ9kP2xQ1",
  "provider": "2",
  "deposit_address": "bc1qar0srrr7xfkvy5l643lydnw9re59gtzzwf5mdq",
  "from_asset": "BTC",
  "from_amount": "0.05",
  "from_amount_usd": null,
  "to_asset": "ETH",
  "to_amount": "0.84219300",
  "expires_at": "2026-07-23T21:45:00Z"
}`,

        notes: [
          'Have your user send exactly from_amount of from_asset to deposit_address before expires_at — after that the order expires unfulfilled.'
        ]
      },

      {
        id: 'status',
        method: 'GET',
        path: '/api/status/{order_id}',
        title: 'Check order status',

        description:
          'Poll this to track a swap from deposit to completion. order_id is the value returned by POST /api/quote.',

        pathParams: [
          {
            name: 'order_id',
            type: 'string',
            required: true,
            desc: 'The order_id from the /api/quote response.'
          }
        ],

        exampleResponse: `{
  "order_id": "aZ9kP2xQ1",
  "status": "pending",
  "message": "Waiting for your deposit...",
  "from_asset": "BTC",
  "to_asset": "ETH",
  "amount_expected": "0.05",
  "amount_deposited": null,
  "amount_received": null,
  "deposit_address": "bc1qar0srrr7xfkvy5l643lydnw9re59gtzzwf5mdq",
  "destination_address": "0x9F3C9a9E9b9C1234567890abcdef1234567890a",
  "refund_address": "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh",
  "created_at": "2026-07-23T21:15:00Z",
  "expires_at": "2026-07-23T21:45:00Z",
  "deposit_instructions": {
    "send_exactly": "0.05",
    "asset": "BTC",
    "to_address": "bc1qar0srrr7xfkvy5l643lydnw9re59gtzzwf5mdq",
    "memo": null,
    "expires_at": "2026-07-23T21:45:00Z",
    "warning": "Send only BTC to this address."
  }
}`,

        notes: [
          'status will be one of: pending, confirming, exchanging, sending, verifying, processing, incomplete, completed, failed, refunded, expired.',
          'Once status is "completed", the response includes a completion object instead of deposit_instructions: { received_amount, tx_hash, sent_to }.'
        ]
      }
    ]
  },

  {
    title: 'XMR > CEX Swaps',

    endpoints: [
      {
        id: 'cex-assets',
        method: 'GET',
        path: '/cex/assets',
        title: 'List coins & exchanges for the XMR path',

        description:
          'The coins you can receive, and which exchanges you can receive them on, for a Monero-only swap.',

        exampleResponse: `{
  "from": {
    "id": "XMR_Monero",
    "assetId": "xmr",
    "networkId": "monero",
    "symbol": "XMR",
    "displayName": "Monero",
    "decimals": 12,
    "limits": { "min": 0.1, "max": 10000 }
  },
  "to": [
    {
      "id": "BTC_Bitcoin",
      "assetId": "btc",
      "networkId": "bitcoin",
      "symbol": "BTC",
      "displayName": "Bitcoin",
      "decimals": 8,
      "limits": { "min": 0.001, "max": 10 }
    }
  ],
  "cex": [
    { "id": "kucoin", "name": "KuCoin", "icon": "\\u{1F535}" }
  ]
}`,

        notes: [
          '"to" and "cex" are trimmed here — the live response lists every supported destination coin and exchange.'
        ]
      },

      {
        id: 'cex-quote',
        method: 'POST',
        path: '/cex/quote',
        title: 'Get a Monero quote',

        description:
          'Price a Monero → coin swap. This path always sends from XMR, so there\'s no from_asset to specify.',

        body: [
          {
            name: 'fromAmount',
            type: 'number',
            required: true,
            desc: 'Amount of XMR to send. Minimum 0.1.'
          },

          {
            name: 'fromAssetId',
            type: 'string',
            required: true,
            desc: 'Always "xmr".'
          },

          {
            name: 'fromNetworkId',
            type: 'string',
            required: true,
            desc: 'Always "monero".'
          },

          {
            name: 'toAssetId',
            type: 'string',
            required: true,
            desc: 'assetId from the to[] list in /cex/assets, e.g. "btc" or "eth".'
          },

          {
            name: 'toNetworkId',
            type: 'string',
            required: true,
            desc: 'networkId from the to[] list in /cex/assets, e.g. "bitcoin" or "ethereum".'
          },

          {
            name: 'toExchangerId',
            type: 'string',
            required: true,
            desc: 'id from the cex[] list in /cex/assets, e.g. "kucoin".'
          }
        ],

        exampleRequest: `POST /cex/quote
Content-Type: application/json

{
  "fromAmount": 1,
  "fromAssetId": "xmr",
  "fromNetworkId": "monero",
  "toAssetId": "btc",
  "toNetworkId": "bitcoin",
  "toExchangerId": "kucoin"
}`,

        exampleResponse: `{
  "quoteId": "q_8f2ac310e7b1"
}`
      },

      {
        id: 'cex-quote-get',
        method: 'GET',
        path: '/cex/quote/{quote_id}',
        title: 'Get quote details',

        description:
          'Fetch the exact output amount for a quote created with POST /cex/quote.',

        pathParams: [
          {
            name: 'quote_id',
            type: 'string',
            required: true,
            desc: 'The quoteId returned from POST /cex/quote.'
          }
        ],

        exampleResponse: `{
  "quoteId": "q_8f2ac310e7b1",
  "quoteLegs": [
    {
      "quoteLegOutput": {
        "toAmount": 0.00345210,
        "toAssetId": "btc",
        "toNetworkId": "bitcoin"
      }
    }
  ]
}`,

        notes: [
          'This is passed through from our liquidity partner largely unchanged, so extra fields may be present beyond the ones shown here.'
        ]
      },

      {
        id: 'cex-order',
        method: 'POST',
        path: '/cex/order',
        title: 'Create a Monero order',

        description:
          'Turns a quote into a real order and returns an XMR deposit address.',

        body: [
          {
            name: 'quoteId',
            type: 'string',
            required: true,
            desc: 'A quoteId from POST /cex/quote.'
          },

          {
            name: 'fromAmount',
            type: 'number',
            required: true,
            desc: 'XMR amount — should match the quote.'
          },

          {
            name: 'fromAssetId',
            type: 'string',
            required: true,
            desc: 'Always "xmr".'
          },

          {
            name: 'fromNetworkId',
            type: 'string',
            required: true,
            desc: 'Always "monero".'
          },

          {
            name: 'toAddress',
            type: 'string',
            required: true,
            desc: 'Address that receives the destination coin.'
          },

          {
            name: 'toAssetId',
            type: 'string',
            required: true,
            desc: 'Same value used in the quote.'
          },

          {
            name: 'toNetworkId',
            type: 'string',
            required: true,
            desc: 'Same value used in the quote.'
          },

          {
            name: 'toExchangerId',
            type: 'string',
            required: true,
            desc: 'id from the cex[] list in /cex/assets, e.g. "kucoin".'
          },

          {
            name: 'refundAddress',
            type: 'string',
            required: false,
            desc: 'XMR address to refund to if the swap fails. Falls back to toAddress if omitted.'
          }
        ],

        exampleRequest: `POST /cex/order
Content-Type: application/json

{
  "quoteId": "q_8f2ac310e7b1",
  "fromAmount": 1,
  "fromAssetId": "xmr",
  "fromNetworkId": "monero",
  "toAddress": "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh",
  "toAssetId": "btc",
  "toNetworkId": "bitcoin",
  "toExchangerId": "kucoin",
  "refundAddress": "4AdUndXHHZ9pfQj27iMAjAmvpG9M6f7DAAvB..."
}`,

        exampleResponse: `{
  "orderId": "o_71f0c9a2",
  "shortId": "71f0c9a2",
  "status": "user_deposit_pending",
  "depositWalletAddress": "4Ab9y1Yw8T1234567890abcdefghijklmno...",
  "expiredAt": "2026-07-23T22:00:00Z",
  "orderInput": { "fromAssetId": "xmr", "fromAmount": 1 },
  "orderOutputs": [
    { "toAssetId": "btc", "toAmount": 0.00345210, "toAddress": "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh" }
  ]
}`,

        notes: [
          'Have your user send exactly fromAmount of XMR to depositWalletAddress before expiredAt.'
        ]
      },

      {
        id: 'cex-order-get',
        method: 'GET',
        path: '/cex/order/{order_id}',
        title: 'Check a Monero order',

        description:
          'Poll this to track a Monero swap from deposit to completion.',

        pathParams: [
          {
            name: 'order_id',
            type: 'string',
            required: true,
            desc: 'The orderId or shortId from POST /cex/order.'
          }
        ],

        exampleResponse: `{
  "orderId": "o_71f0c9a2",
  "shortId": "71f0c9a2",
  "status": "provider_order_complete",
  "statusShort": "processing",
  "statusText": "Sending to destination",
  "depositWalletAddress": "4Ab9y1Yw8T1234567890abcdefghijklmno...",
  "depositAmount": 1,
  "expiredAt": "2026-07-23T22:00:00Z",
  "createdAt": "2026-07-23T21:30:00Z",
  "updatedAt": "2026-07-23T21:42:00Z",
  "orderInput": { "fromAssetId": "xmr", "fromAmount": 1 },
  "orderOutputs": [
    { "toAssetId": "btc", "toAmount": 0.00345210, "toAddress": "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh" }
  ],
  "orderLegs": [
    { "depositTxHash": "5b1f0...e93a" }
  ]
}`,

        notes: [
          'status will be one of: user_deposit_pending, pending_provider_order, provider_order_complete, completed, failed, expired.'
        ]
      }
    ]
  }
];

/* ============================================================
   SERVICES CONFIGURATION
   ============================================================ */

const SERVICES = [
  {
    id: 'website',

    title: 'Custom Exchange Website',

    icon: <Globe size={24} />,

    description:
      'A complete, branded cryptocurrency exchange website built with React or vanilla HTML/JS. Includes your logo, custom domain, and full API integration.',

    features: [
      'Responsive web design',
      'Real-time price updates',
      'Order tracking dashboard',
      'Admin panel for monitoring',
      'Custom theming & branding',
      'SEO optimized'
    ],

    templates: [
      {
        name: 'Modern Minimal',
        img: '/templates/web-minimal.jpg'
      },

      {
        name: 'Crypto Dark',
        img: '/templates/web-dark.jpg'
      },

      {
        name: 'Clean Light',
        img: '/templates/web-light.jpg'
      }
    ],

    price: '$2,500+',

    delivery: '5-7 days',

    popular: true
  },

  {
    id: 'discord',

    title: 'Discord Exchange Bot',

    icon: <Bot size={24} />,

    description:
      'A full-featured Discord bot that allows users to swap crypto directly in your server. I can integrate whatever exchange, swap, aggregator, or crypto API you want to use for processing swaps, along with slash commands, DM support, and role-based permissions.',

    features: [
      'Slash commands (/swap, /price, /status)',
      'Integrate any swap/exchange API you choose',
      'Integrate aggregators or multiple APIs',
      'DM & channel support',
      'Role-based access control',
      'Transaction notifications',
      'Server economy integration',
      '24/7 hosting included'
    ],

    templates: [
      {
        name: 'Standard Bot',
        img: '/templates/discord-standard.jpg'
      },

      {
        name: 'Premium UI',
        img: '/templates/discord-premium.jpg'
      }
    ],

    price: '$1,200+',

    delivery: '3-5 days',

    popular: false
  },

  {
    id: 'telegram',

    title: 'Telegram Exchange Bot',

    icon: <MessageCircle size={24} />,

    description:
      'A Telegram bot with inline keyboards and commands for instant crypto swaps. I can integrate whatever exchange, swap, aggregator, or crypto API you want to use for your swaps, with support for groups, channels, private chats, and webhook integration.',

    features: [
      'Inline keyboards & menus',
      'Integrate any swap/exchange API you choose',
      'Integrate aggregators or multiple APIs',
      'Group & private chat support',
      'Instant swap notifications',
      'Referral system integration',
      'Multi-language support',
      'Webhook hosting included'
    ],

    templates: [
      {
        name: 'Classic Bot',
        img: '/templates/telegram-classic.jpg'
      },

      {
        name: 'Modern UI',
        img: '/templates/telegram-modern.jpg'
      }
    ],

    price: '$1,000+',

    delivery: '3-4 days',

    popular: false
  },

  {
    id: 'development-support',

    title: 'Development & Support Work',

    icon: <Code2 size={24} />,

    description:
      "Looking for a developer or support agent for your existing crypto project? I'm available for freelance development, API integrations, bug fixes, maintenance, customer support, and ongoing technical work.",

    features: [
      'Frontend & backend development',
      'API integrations & troubleshooting',
      'Bug fixes & maintenance',
      'Crypto exchange support',
      'Customer/support agent work',
      'Ongoing or contract-based work'
    ],

    templates: [],

    price: 'Contact for pricing',

    delivery: 'Flexible',

    popular: false
  }
];

const TELEGRAM_USERNAME = 'iceswapsupport';

/* ============================================================
   TEMPLATE MODAL
   ============================================================ */

const TemplateModal = ({ service, onClose }) => {
  if (!service) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-content template-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>
            {service.title} - Templates
          </h3>

          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {service.templates.length > 0 && (
            <div className="template-grid">
              {service.templates.map(
                (template, idx) => (
                  <div
                    key={idx}
                    className="template-card-preview"
                  >
                    <div className="template-preview-box">
                      <div className="template-placeholder">
                        {service.icon}

                        <span>
                          {template.name}
                        </span>
                      </div>
                    </div>

                    <div className="template-name">
                      {template.name}
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          <div className="template-features">
            <h4>What's Included:</h4>

            <ul>
              {service.features.map(
                (feature, idx) => (
                  <li key={idx}>
                    <CheckCircle2 size={16} />
                    {feature}
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="template-cta">
            <div className="price-tag">
              {service.price}
            </div>

            <div className="delivery-time">
              Delivery: {service.delivery}
            </div>

            <a
              href={`https://t.me/${TELEGRAM_USERNAME}`}
              target="_blank"
              rel="noopener noreferrer"
              className="telegram-cta-btn"
            >
              <MessageCircle size={18} />
              Order via Telegram
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   SERVICE CARD
   ============================================================ */

const ServiceCard = ({ service }) => {
  const isDevelopmentSupport =
    service.id === 'development-support';

  return (
    <div
      className={`service-card ${
        service.popular ? 'popular' : ''
      } ${
        isDevelopmentSupport
          ? 'development-support-card'
          : ''
      }`}
    >
      {service.popular && (
        <div className="popular-badge">
          Most Popular
        </div>
      )}

      <div className="service-icon">
        {service.icon}
      </div>

      <h3>{service.title}</h3>

      <p className="service-desc">
        {service.description}
      </p>

      <div className="service-features-preview">
        {service.features
          .slice(0, 3)
          .map((feat, idx) => (
            <span
              key={idx}
              className="feature-tag"
            >
              {feat}
            </span>
          ))}

        {service.features.length > 3 && (
          <span className="feature-tag">
            +{service.features.length - 3} more
          </span>
        )}
      </div>

      <div className="service-price-row">
        <div className="price">
          {service.price}
        </div>

        <div className="delivery">
          {service.delivery}
        </div>
      </div>

      <div className="service-actions">
        <a
          href={`https://t.me/${TELEGRAM_USERNAME}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
        >
          <MessageCircle size={16} />

          {isDevelopmentSupport
            ? 'Contact Me'
            : 'Get Started'}
        </a>
      </div>
    </div>
  );
};

/* ============================================================
   API DOCS
   ============================================================ */

const ApiDocs = () => {
  const [selectedService, setSelectedService] =
    useState(null);

  const [showServicesBanner, setShowServicesBanner] =
    useState(true);

  const scrollToServices = () => {
    document
      .getElementById('custom-dev-section')
      ?.scrollIntoView({
        behavior: 'smooth'
      });
  };

  return (
    <div className="docs-page">

      {/* ======================================================
          FLOATING CONTACT BUTTON
          ====================================================== */}

      <a
        href={`https://t.me/${TELEGRAM_USERNAME}`}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-contact-btn"
        title="Contact on Telegram"
      >
        <MessageCircle size={24} />
      </a>

      {/* ======================================================
          TOP BAR
          ====================================================== */}

      <div className="docs-topbar">
        <div className="docs-topbar-left">
          <span
            className="brand-mark"
            style={{
              width: 32,
              height: 32
            }}
          >
            <Snowflake size={15} />
          </span>

          <div>
            <h1>Ice Swap API</h1>

            <div className="docs-topbar-sub">
              Documentation & development pages
            </div>
          </div>
        </div>

        <Link
          to="/"
          className="docs-back-link"
        >
          <ArrowLeft size={15} />
          Back to site
        </Link>
      </div>

      {/* ======================================================
          SERVICES BANNER
          ====================================================== */}

      {showServicesBanner && (
        <div className="services-banner">
          <div className="services-banner-content">
            <div className="services-banner-text">
              <h3>
                Need a custom exchange built for you?
              </h3>

              <p>
                I build white-label crypto exchange
                websites, Discord bots, and Telegram bots
                using whatever swap, exchange, aggregator,
                or crypto APIs you want integrated.
              </p>
            </div>

            <div className="services-banner-actions">
              <button
                className="btn-banner-primary"
                onClick={scrollToServices}
              >
                View Services & Pricing
              </button>

              <button
                className="btn-banner-close"
                onClick={() =>
                  setShowServicesBanner(false)
                }
                aria-label="Close services banner"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          DOCUMENTATION LAYOUT
          ====================================================== */}

      <div className="docs-layout">

        {/* ====================================================
            SIDEBAR
            ==================================================== */}

        <nav className="docs-sidebar">
          {SECTIONS.map((section) => (
            <div
              className="docs-nav-group"
              key={section.title}
            >
              <div className="docs-nav-group-title">
                {section.title}
              </div>

              {section.endpoints.map((ep) => (
                <a
                  href={`#${ep.id}`}
                  className="docs-nav-link"
                  key={ep.id}
                >
                  <span
                    className={`docs-nav-method ${ep.method.toLowerCase()}`}
                  >
                    {ep.method}
                  </span>

                  {ep.title}
                </a>
              ))}
            </div>
          ))}

          {/* Services */}

          <div className="docs-nav-group">
            <div className="docs-nav-group-title">
              Services
            </div>

            <a
              href="#custom-dev-section"
              className="docs-nav-link"
            >
              <span className="docs-nav-method get">
                NEW
              </span>

              Custom Development
            </a>
          </div>
        </nav>

        {/* ====================================================
            CONTENT
            ==================================================== */}

        <div className="docs-content">

          {/* ==================================================
              INTRO
              ================================================== */}

          <div className="docs-intro">
            <h2>Integrate Ice Swap</h2>

            <p>
              These are the same endpoints our own
              frontend calls, so anything you can do in
              the app, you can do from your own backend
              or product.
            </p>

            <p>
              No API key is required — every endpoint
              below is open to call directly.
            </p>

            <div className="docs-base-url">
              Base URL: {BASE_URL_EXAMPLE}
            </div>

            <div className="docs-note info">
              <Info size={15} />

              <span>
                Errors use standard HTTP status codes
                with a JSON body of{' '}
                <code>
                  {'{ "detail": "..." }'}
                </code>
                . Expect <code>400</code> for bad input
                or an unavailable rate, <code>404</code>{' '}
                for an order or quote that doesn't
                exist, and <code>502</code> if an
                upstream liquidity provider is
                unreachable.
              </span>
            </div>
          </div>

          {/* ==================================================
              API ENDPOINTS
              ================================================== */}

          {SECTIONS.map((section) => (
            <div key={section.title}>
              {section.endpoints.map((ep) => (
                <Endpoint
                  ep={ep}
                  key={ep.id}
                />
              ))}
            </div>
          ))}

          {/* ==================================================
              CUSTOM DEVELOPMENT SECTION
              ================================================== */}

          <section
            className="docs-endpoint custom-dev-section"
            id="custom-dev-section"
          >
            <div className="custom-dev-header">
              <h2>
                Custom Development Services
              </h2>

              <p className="custom-dev-subtitle">
                Don't want to build it yourself? I'll
                create a fully functional exchange for
                you — or help with an existing project.
              </p>
            </div>

            {/* SERVICE CARDS */}

            <div className="services-grid">
              {SERVICES.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onSelect={setSelectedService}
                />
              ))}
            </div>

            {/* ==================================================
                PROCESS
                ================================================== */}

            <div className="process-section">
              <h3>How It Works</h3>

              <div className="process-steps">

                <div className="process-step">
                  <div className="step-number">
                    1
                  </div>

                  <h4>Contact</h4>

                  <p>
                    Message me on Telegram with your
                    requirements
                  </p>
                </div>

                <div className="process-step">
                  <div className="step-number">
                    2
                  </div>

                  <h4>Quote</h4>

                  <p>
                    I'll provide a detailed quote and
                    timeline
                  </p>
                </div>

                <div className="process-step">
                  <div className="step-number">
                    3
                  </div>

                  <h4>Development</h4>

                  <p>
                    I build your custom exchange with
                    regular updates
                  </p>
                </div>

                <div className="process-step">
                  <div className="step-number">
                    4
                  </div>

                  <h4>Delivery</h4>

                  <p>
                    You receive the source code and
                    deployment help
                  </p>
                </div>

              </div>
            </div>

            {/* ==================================================
                PAYMENT INFO
                ================================================== */}

            <div className="payment-info">
              <div className="payment-icon">
                <Wallet size={24} />
              </div>

              <div className="payment-details">
                <h4>Payment Terms</h4>

                <p>
                  One-time fee per project. 50% upfront
                  to start, 50% on delivery. Crypto
                  payments accepted (BTC, ETH, USDT,
                  XMR). All deals include ongoing support
                  & any updates you want at an addon fee
                  of at least $100.
                </p>
              </div>
            </div>

            {/* ==================================================
                FINAL CTA
                ================================================== */}

            <div className="final-cta">
              <h3>
                Looking for development or support work?
              </h3>

              <p>
                I'm also available for freelance
                development, maintenance, API
                integration, technical support, and
                support agent roles.
              </p>

              <a
                href={`https://t.me/${TELEGRAM_USERNAME}`}
                target="_blank"
                rel="noopener noreferrer"
                className="telegram-main-cta"
              >
                <MessageCircle size={20} />

                <span>
                  Message @{TELEGRAM_USERNAME}
                </span>

                <ExternalLink size={16} />
              </a>
            </div>
          </section>
        </div>
      </div>

      {/* ======================================================
          TEMPLATE MODAL
          ====================================================== */}

      {selectedService && (
        <TemplateModal
          service={selectedService}
          onClose={() =>
            setSelectedService(null)
          }
        />
      )}
    </div>
  );
};

export default ApiDocs;